import type { CSSObject } from '@emotion/react'
import {
  Color,
  Size,
  Spacing,
  Theme,
  Transition,
  Typography,
} from '@opallabs/gem'
import type { StyleProps } from './types'

const rootClass = 'lapidary-textinput'
export const classes = {
  container: `${rootClass}-container`,
  input: `${rootClass}-input`,
  errorMessage: `${rootClass}-errorMessage`,
} as const
export const states = {
  disabled: 'disabled',
  hasValue: 'has-value',
}

export const container = ({
  theme,
  modified,
  invalid,
  active,
}: {
  theme: Theme
  modified: boolean
  invalid: boolean
  active: boolean
}): CSSObject => ({
  alignItems: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'nowrap',
  position: 'relative',
  gap: Spacing.S,
  paddingInline: Spacing.S,
  borderRadius: Size.Rounding,

  ...Typography.T500,
  ...Transition(['background-color', 'border-color', 'color']),

  backgroundColor: active
    ? Color[theme].Surface.N000
    : theme === 'dark'
      ? Color[theme].Surface.N200
      : Color[theme].Surface.N100,
  border: `1px solid ${Color[theme].Surface.N300}`,
  color: Color[theme].Text.Default,
  ...(modified &&
    invalid && {
      border: `1px solid ${Color[theme].Surface.R100}`,
    }),

  ...(!invalid && {
    '&:focus-within': {
      borderColor: Color[theme].Surface.A200,
    },
  }),

  ...(invalid && {
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${Color[theme].Surface.A200}`,
    },
  }),

  '&:hover': {
    backgroundColor: Color[theme].Translucent.A08,
  },
})

export const input = ({ theme }: { theme: Theme }): CSSObject => ({
  background: 'transparent',
  border: 'none',
  blockSize: Spacing.M * 2 - 2, // -2px to account for border on parent so height is still 32px
  display: 'flex',
  fill: 'currentColor',
  inlineSize: '100%',
  inlinePadding: 0,
  blockPadding: 0,
  padding: 0,
  minInlineSize: Spacing.XS * 37,
  ...Typography.T500,
  color: Color[theme].Text.Default,
  outline: 'none',

  '&::placeholder-shown': {
    color: Color[theme].Text.Gray,
  },

  '&:disabled': {
    opacity: 0.5,
  },

  // @see https://stackoverflow.com/a/62162805
  '::-webkit-calendar-picker-indicator':
    theme === 'light'
      ? null
      : {
          filter: 'invert(1)',
        },

  // Remove webkit native search input styling for our own
  '&[type="search"]::-webkit-search-cancel-button': { appearance: 'none' },
})

export const validationError = ({ theme }: StyleProps): CSSObject => ({
  display: 'flex',
  ...Typography.T300,
  marginBlockStart: Spacing.XS / 2,
  color: Color[theme].Text.Warning,
})

export const leadingIconContainer: CSSObject = {
  inlineSize: Spacing.M,
}

export const leadingIcon = ({
  active,
  theme,
  disabled,
}: StyleProps & {
  active: boolean
}): CSSObject => ({
  opacity: disabled ? 0.5 : 1,
  color: active ? Color[theme].Surface.N600 : Color[theme].Text.Default,
})

export const characterCounter = ({ theme }: StyleProps): CSSObject => ({
  color: Color[theme].Text.Gray,
})

// keeps tailing icon & clear button aligned if
// both are being used
export const trailingIconContainer: CSSObject = {
  alignItems: 'center',
  display: 'flex',
  blockSize: Spacing.L,
  paddingInline: Spacing.XS,
}

export const trailingIcon = ({ theme, disabled }: StyleProps): CSSObject => ({
  opacity: disabled ? 0.5 : 1,
  color: Color[theme].Surface.N999,
})

export const clearableButton = ({ theme }: { theme: Theme }): CSSObject => ({
  blockSize: Spacing.L,
  inlineSize: Spacing.L,
  color: Color[theme].Text.Default,
})

export const helpText = (theme: Theme): CSSObject => ({
  ...Typography.T300,
  color: Color[theme].Text.Gray,
})
